/**
 * Sounds, synthesised in code.
 *
 * The same bargain as the block tiles and the skins: no binary art in the
 * repository, every sound tweakable by changing a number, and - because this
 * file touches no DOM and no engine - renderable headlessly, so a sound can be
 * written out as a .wav and listened to without launching the game.
 *
 * Everything here is deterministic. A seed goes in, the same samples come out,
 * which is what lets the exported file and the sound the game plays be the
 * same sound, and lets the tests assert on the waveform at all. Variation
 * between footsteps comes from passing a different seed, never from an
 * unseeded random.
 *
 * The vocabulary is small on purpose: noise, a couple of oscillators, one
 * filter and an envelope. Nearly every impact in a game is a burst of filtered
 * noise plus a pitched thud underneath it, and the difference between wood,
 * stone and glass is mostly which frequencies survive and how fast it dies.
 */

/** CD rate. Web Audio resamples anything else, so there is nothing to gain. */
export const SAMPLE_RATE = 44100;

export interface Sound {
  sampleRate: number;
  /** Mono, -1..1. Mono because a positioned sound gets panned at playback. */
  samples: Float32Array;
}

export type SoundName =
  | "breakWood" | "breakStone" | "breakGrass" | "breakGlass" | "place";

/**
 * A seeded generator, so "random" noise is reproducible.
 *
 * mulberry32: small, fast, and good enough for noise. Math.random() would
 * make every export different and every test meaningless.
 */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A state-variable filter: low-pass, band-pass and high-pass at once.
 *
 * One filter covers every sound here because the three outputs come out of the
 * same two integrators - so wood (band-pass, low) and glass (high-pass) differ
 * by which output is read, not by which filter is used.
 *
 * `f` must stay well below 1 or the filter blows up; the cutoff is clamped to
 * a fifth of the sample rate for exactly that reason.
 */
export class Filter {
  private low = 0;
  private band = 0;
  private readonly f: number;
  private readonly q: number;

  constructor(cutoffHz: number, resonance: number) {
    const limit = SAMPLE_RATE / 5;
    this.f = 2 * Math.sin((Math.PI * Math.min(cutoffHz, limit)) / SAMPLE_RATE);
    // Stored inverted: a higher `resonance` should mean a sharper peak.
    this.q = 1 / Math.max(0.5, resonance);
  }

  step(x: number): { low: number; band: number; high: number } {
    this.low += this.f * this.band;
    const high = x - this.low - this.q * this.band;
    this.band += this.f * high;
    return { low: this.low, band: this.band, high };
  }
}

/**
 * Attack-then-decay, in seconds.
 *
 * The attack is never zero. A burst that starts at full amplitude on sample
 * one is a step discontinuity, and a step discontinuity is a click - audible
 * on top of the sound as a hard tick, and the first thing that makes synthesis
 * sound cheap. One or two milliseconds is enough to remove it and short enough
 * that the hit still feels sharp.
 */
export function envelope(t: number, attack: number, tau: number): number {
  if (t < attack) return t / attack;
  return Math.exp(-(t - attack) / tau);
}

/** Scale to just under full range, so nothing clips when voices overlap. */
export function normalize(samples: Float32Array, peak = 0.9): void {
  let max = 0;
  for (const s of samples) max = Math.max(max, Math.abs(s));
  if (max === 0) return;
  const gain = peak / max;
  for (let i = 0; i < samples.length; i++) samples[i]! *= gain;
}

/**
 * Ramp the last few milliseconds down to silence.
 *
 * A decaying envelope never quite reaches zero, and cutting the buffer at a
 * non-zero sample is the same click as starting at one - heard at the end of
 * the sound instead of the beginning.
 *
 * None of the five sounds here actually needs it: all of them decay below
 * audibility before the buffer ends, which means the "ends at zero" assertion
 * in the tests cannot currently fail. It is exported so the ramp itself can be
 * tested on a signal that does need it - a bell, or a looping ambience, will.
 */
export function fadeOut(samples: Float32Array, seconds = 0.004): void {
  const n = Math.min(samples.length, Math.round(seconds * SAMPLE_RATE));
  for (let i = 0; i < n; i++) {
    // (i + 1) / n, not i / n: the latter leaves the final sample at 1/n of
    // its value instead of at zero, so the ramp ends on a step of exactly the
    // size it exists to remove. Quiet, but it is the same click.
    samples[samples.length - n + i]! *= 1 - (i + 1) / n;
  }
}

export function buffer(seconds: number): Float32Array {
  return new Float32Array(Math.round(seconds * SAMPLE_RATE));
}

/**
 * A struck, dull thing: an axe into a trunk.
 *
 * Two layers. The body is noise squeezed into a narrow band low down, which is
 * what "wooden" means to the ear - wood resonates in a couple of hundred hertz
 * and swallows everything above it. Under that sits a pitched thump that falls
 * as it decays, because a struck body of wood loses tension as it stops
 * ringing, and a thump at a constant pitch reads as a drum machine.
 */
export function breakWood(seed = 1): Sound {
  const out = buffer(0.16);
  const noise = rng(seed);
  const body = new Filter(520, 3.2);
  const knock = new Filter(1400, 1.0);

  for (let i = 0; i < out.length; i++) {
    const t = i / SAMPLE_RATE;
    const white = noise() * 2 - 1;

    const band = body.step(white).band * envelope(t, 0.001, 0.035) * 1.4;
    // A short, brighter tick on top: the axe itself, not the trunk.
    const tick = knock.step(white).band * envelope(t, 0.0005, 0.006) * 0.5;
    // The thud, falling from 190 Hz to about 110 Hz as it dies.
    const hz = 190 * Math.exp(-t * 9) + 105;
    const thud = Math.sin(2 * Math.PI * hz * t) * envelope(t, 0.002, 0.05) * 0.7;

    out[i] = band + tick + thud;
  }
  normalize(out);
  fadeOut(out);
  return { sampleRate: SAMPLE_RATE, samples: out };
}

/**
 * Stone: brighter, harder, and it rings a little longer than wood.
 *
 * The band sits nearly two octaves higher and the decay is slower, which is
 * the whole difference. Same two layers otherwise - proof that the material is
 * in the numbers rather than in the code.
 */
export function breakStone(seed = 2): Sound {
  const out = buffer(0.22);
  const noise = rng(seed);
  const body = new Filter(1750, 2.4);
  const grit = new Filter(4200, 1.0);

  for (let i = 0; i < out.length; i++) {
    const t = i / SAMPLE_RATE;
    const white = noise() * 2 - 1;

    const band = body.step(white).band * envelope(t, 0.001, 0.055) * 1.2;
    // Chips and dust flying off, sprayed across the top end.
    const dust = grit.step(white).high * envelope(t, 0.001, 0.09) * 0.35;
    const hz = 140 * Math.exp(-t * 12) + 78;
    const thud = Math.sin(2 * Math.PI * hz * t) * envelope(t, 0.002, 0.045) * 0.8;

    out[i] = band + dust + thud;
  }
  normalize(out);
  fadeOut(out);
  return { sampleRate: SAMPLE_RATE, samples: out };
}

/**
 * Grass and foliage: no pitch at all, just a rustle.
 *
 * High-passed noise with nothing underneath it. Adding a thud here is the
 * mistake that makes grass sound like a drum - torn leaves have no body to
 * resonate. The slow amplitude wobble is what separates a rustle from a hiss.
 */
export function breakGrass(seed = 3): Sound {
  const out = buffer(0.13);
  const noise = rng(seed);
  const body = new Filter(2600, 0.9);

  for (let i = 0; i < out.length; i++) {
    const t = i / SAMPLE_RATE;
    const white = noise() * 2 - 1;
    const wobble = 1 + 0.35 * Math.sin(2 * Math.PI * 62 * t);
    out[i] = body.step(white).high * envelope(t, 0.003, 0.03) * wobble;
  }
  normalize(out, 0.7); // quieter than an axe blow, because it is
  fadeOut(out);
  return { sampleRate: SAMPLE_RATE, samples: out };
}

/**
 * Glass: a spray of shards.
 *
 * The one sound here that is not noise-plus-thud. Broken glass is a handful of
 * high, unrelated tones - so the partials are deliberately inharmonic: ratios
 * with no common divisor, because anything harmonic would come out as a musical
 * note. Each shard decays at its own rate, and the shortest ones first, which
 * is what gives the tail its shimmer.
 */
export function breakGlass(seed = 4): Sound {
  const out = buffer(0.42);
  const noise = rng(seed);
  const burst = new Filter(5200, 1.0);
  const shards = [
    { hz: 3180, tau: 0.10, gain: 0.9 },
    { hz: 4470, tau: 0.14, gain: 0.7 },
    { hz: 5310, tau: 0.07, gain: 0.6 },
    { hz: 6890, tau: 0.16, gain: 0.5 },
    { hz: 8230, tau: 0.05, gain: 0.4 },
  ];

  for (let i = 0; i < out.length; i++) {
    const t = i / SAMPLE_RATE;
    const white = noise() * 2 - 1;

    let sum = burst.step(white).high * envelope(t, 0.0005, 0.012) * 0.8;
    for (const s of shards) {
      sum += Math.sin(2 * Math.PI * s.hz * t) * envelope(t, 0.001, s.tau) * s.gain;
    }
    out[i] = sum * 0.35;
  }
  normalize(out);
  fadeOut(out);
  return { sampleRate: SAMPLE_RATE, samples: out };
}

/**
 * Putting a block down: the same thud as breaking, without the debris.
 *
 * Shorter and softer than any of the breaks, because placing happens as often
 * as breaking and a loud sound repeated is the fastest way to make a player
 * turn the audio off.
 */
export function place(seed = 5): Sound {
  const out = buffer(0.1);
  const noise = rng(seed);
  const body = new Filter(900, 1.6);

  for (let i = 0; i < out.length; i++) {
    const t = i / SAMPLE_RATE;
    const white = noise() * 2 - 1;
    const band = body.step(white).band * envelope(t, 0.001, 0.02) * 0.9;
    const hz = 260 * Math.exp(-t * 14) + 130;
    const thud = Math.sin(2 * Math.PI * hz * t) * envelope(t, 0.002, 0.03) * 0.8;
    out[i] = band + thud;
  }
  normalize(out, 0.65);
  fadeOut(out);
  return { sampleRate: SAMPLE_RATE, samples: out };
}

/** Every sound the game knows how to make, by name. */
export const SYNTHS: Readonly<Record<SoundName, (seed?: number) => Sound>> = {
  breakWood, breakStone, breakGrass, breakGlass, place,
};
